import { Trans } from "@lingui/react/macro";
import { FeatureFlagKey } from "@regrello/feature-flags-api";
import { RegrelloMenuV2ItemContent } from "@regrello/ui-core";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";
import { useMount } from "react-use";

import { ValidationRules } from "../../../../../../constants/globalConstants";
import { FeatureFlagService } from "../../../../../../services/FeatureFlagService";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../../views/modals/formDialogs/spectrumFields/_internal/ConfigureSpectrumFieldForm";
import {
  RegrelloControlledFormFieldNumber,
  RegrelloControlledFormFieldSelect,
} from "../../../../formFields/controlled/regrelloControlledFormFields";
import { getDecimalPlaceMenuOptions, getDecimalPlaceOption } from "../../../utils/decimalPlaceOptionUtils";
import {
  getDisallowGroupingsFloatMenuOptions,
  getDisallowGroupingsFloatOption,
} from "../../../utils/disallowGroupingsFloatOptionUtils";
import { findValueConstraint } from "../../../utils/findValueConstraint";
import {
  FrontendValueConstraintRuleName,
  getValueConstraintDisplayName,
} from "../../../utils/spectrumFieldConstraintUtils";

/** The default number of decimal places for Currency fields. */
const DEFAULT_CURRENCY_DECIMAL_PLACES = 2;

/**
 * Renders inputs for the currency field's various value constraints.
 */
export function SpectrumCurrencyValueConstraints({
  constraints,
  disabled,
  form,
}: {
  constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
  disabled: boolean;
  form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
}): React.ReactNode {
  const getFieldKey = (index: number) => `valueConstraints.${index}.args.0` as const;

  function findConstraint<TValue = number>(
    ruleName: FrontendValueConstraintRuleName,
    parseValue?: (value: string) => TValue,
  ) {
    return findValueConstraint<TValue>(form, constraints, ruleName, parseValue);
  }

  // Find constraints in the react-hook-form.
  const minFloatConstraint = findConstraint(FrontendValueConstraintRuleName.MIN_FLOAT);
  const maxFloatConstraint = findConstraint(FrontendValueConstraintRuleName.MAX_FLOAT);
  const decimalPlacesConstraint = findConstraint(FrontendValueConstraintRuleName.DECIMAL_PLACES);
  const disallowGroupingsFloatConstraint = findConstraint(
    FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT,
    parseBoolean,
  );

  // Check if the currency field improvements feature flag is enabled.
  const isNumberAndCurrencyFieldImprovementsEnabled = FeatureFlagService.isEnabled(
    FeatureFlagKey.NUMBER_AND_CURRENCY_FIELD_IMPROVEMENTS_2025_03,
  );

  useMount(() => {
    // Default the decimal places constraint to 2 for Currency fields if it's not set.
    if (!Number.isFinite(decimalPlacesConstraint.value)) {
      form.setValue("valueConstraintsComplex.decimalPlaces", getDecimalPlaceOption(DEFAULT_CURRENCY_DECIMAL_PLACES));
    }

    // Default the disallow_groupings_float constraint to false if it's not set.
    if (disallowGroupingsFloatConstraint.value == null) {
      form.setValue("valueConstraintsComplex.disallowGroupingsFloat", getDisallowGroupingsFloatOption(false));
    }
  });

  return (
    <>
      {constraints.map((row, constraintIndex) => {
        if (row.constraint.valueConstraintRule === FrontendValueConstraintRuleName.DECIMAL_PLACES) {
          if (!isNumberAndCurrencyFieldImprovementsEnabled) {
            return null;
          }

          // Allow users to specify the number of decimal places.
          return (
            <RegrelloControlledFormFieldSelect
              key={row.constraint.uuid}
              controllerProps={{
                control: form.control,
                name: "valueConstraintsComplex.decimalPlaces",
              }}
              disabled={disabled}
              getOptionLabel={(option) => option.label}
              isFilterable={false}
              label={getValueConstraintDisplayName(row.constraint)}
              options={getDecimalPlaceMenuOptions()} // (clewis): This is O(N) but it's only a few items so it's fine.
              renderOption={(option) => {
                return <RegrelloMenuV2ItemContent endElement={option.preview} text={option.label} />;
              }}
              renderSelectedValue={(option) => {
                return option == null ? null : option.label;
              }}
              suppressOptionPadding={true}
            />
          );
        }

        if (row.constraint.valueConstraintRule === FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT) {
          // The disallow_groupings_float constraint is visible only when the feature flag is on.
          if (!isNumberAndCurrencyFieldImprovementsEnabled) {
            return null;
          }

          return (
            <RegrelloControlledFormFieldSelect
              key={row.constraint.uuid}
              controllerProps={{
                control: form.control,
                name: "valueConstraintsComplex.disallowGroupingsFloat",
              }}
              disabled={disabled}
              getOptionLabel={(option) => option.label}
              isFilterable={false}
              label={getValueConstraintDisplayName(row.constraint)}
              options={getDisallowGroupingsFloatMenuOptions()}
              renderOption={(option) => {
                return <RegrelloMenuV2ItemContent endElement={option.preview} text={option.label} />;
              }}
              renderSelectedValue={(option) => {
                if (option == null) {
                  return null;
                }
                const label = option.label;
                const preview = option.preview;

                return (
                  <span>
                    {label}{" "}
                    <span className="text-textMuted">
                      (<Trans>Example: {preview}</Trans>)
                    </span>
                  </span>
                );
              }}
              suppressOptionPadding={true}
            />
          );
        }

        const fieldKey = getFieldKey(constraintIndex);

        return (
          <RegrelloControlledFormFieldNumber
            key={row.constraint.uuid}
            controllerProps={{
              control: form.control,
              name: fieldKey,
              rules:
                // If there's a minFloat constraint, prevent the max value from going below it.
                constraintIndex === minFloatConstraint.index && maxFloatConstraint.value != null
                  ? {
                      ...ValidationRules.NUMBER_FROM_UNKNOWN,
                      ...ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND(maxFloatConstraint.value),
                      ...ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND(Number.MIN_SAFE_INTEGER),
                    }
                  : // If there's a maxFloat constraint, prevent the min value from going below it.
                    constraintIndex === maxFloatConstraint.index && minFloatConstraint.value != null
                    ? {
                        ...ValidationRules.NUMBER_FROM_UNKNOWN,
                        ...ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND(Number.MAX_SAFE_INTEGER),
                        ...ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND(minFloatConstraint.value),
                      }
                    : // If neither is set, let both values be effectively unbounded.
                      {
                        ...ValidationRules.NUMBER_FROM_UNKNOWN,
                        ...ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND(Number.MAX_SAFE_INTEGER),
                        ...ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND(Number.MIN_SAFE_INTEGER),
                      },
            }}
            disabled={disabled}
            label={getValueConstraintDisplayName(row.constraint)}
          />
        );
      })}
    </>
  );
}

function parseBoolean(value: string) {
  return value === "true" ? true : value === "false" ? false : null;
}
